/* eslint-disable max-lines */
import ConfidenceLevel from "@/constants/ConfidenceLevel";
import GoalType from "@/constants/GoalType";
import KeyResultMeasurement from "@/constants/KeyResultMeasurement";
import KeyResultBinaryChoice from "@/constants/KeyResultBinaryChoice";
import { i18n } from "@/i18n";
import { initWebSocketListeners } from "./gokrApiOrchestrator";

initWebSocketListeners();

/**
 * @typedef {Object} KeyResultCounts
 * @prop {String} goalId
 * @prop {Number} keyResultCount
 * @prop {Number} completeCount
 * @prop {Number} incompleteCount
 * @prop {Number} progress
 */

/**
 * @typedef {Object} GoalTypeCounts
 * @prop {Number} ACTIVE
 * @prop {Number} ARCHIVED
 * @prop {Number} DRAFT
 */

/**
 * @typedef {Object} InitialGoalMetadata
 * @prop {Number} activeGoalCount
 * @prop {Number} archivedGoalCount
 * @prop {Number} draftGoalCount
 * @prop {Number} goalCount
 * @prop {GoalTypeCounts} goalCounts
 */

const createEmptyMetadata = () => {
	/**
	 * @type InitialGoalMetadata
	 */
	return {
		goalCounts: {
			[ GoalType.ACTIVE ]: 0,
			[ GoalType.ARCHIVED ]: 0,
			[ GoalType.DRAFT ]: 0,
		},
	};
};

export default {
	//
	// constants
	//
	confidenceLevelMap: {
		[ ConfidenceLevel.HIGH ]: i18n.t( "gokr.ConfidenceLevel_HIGH" ),
		[ ConfidenceLevel.MEDIUM ]: i18n.t( "gokr.ConfidenceLevel_MEDIUM" ),
		[ ConfidenceLevel.LOW ]: i18n.t( "gokr.ConfidenceLevel_LOW" ),
	},
	goalTypeMap: {
		[ GoalType.ACTIVE ]: i18n.t( "gokr.GoalType_ACTIVE" ),
		[ GoalType.DRAFT ]: i18n.t( "gokr.GoalType_DRAFT" ),
		[ GoalType.ARCHIVED ]: i18n.t( "gokr.GoalType_ARCHIVE" ),
	},
	keyResultMeasurementMap: {
		[ KeyResultMeasurement.NUMERIC ]: i18n.t( "gokr.KeyResultMeasurement_NUMERIC" ),
		[ KeyResultMeasurement.PERCENTAGE ]: i18n.t( "gokr.KeyResultMeasurement_PERCENTAGE" ),
		[ KeyResultMeasurement.CURRENCY ]: i18n.t( "gokr.KeyResultMeasurement_CURRENCY" ),
		[ KeyResultMeasurement.BINARY ]: i18n.t( "gokr.KeyResultMeasurement_BINARY" ),
	},
	keyResultBinaryChoiceMap: {
		[ KeyResultBinaryChoice.COMPLETE ]: i18n.t( "gokr.KeyResultBinaryChoice_COMPLETE" ),
		[ KeyResultBinaryChoice.INCOMPLETE ]: i18n.t( "gokr.KeyResultBinaryChoice_INCOMPLETE" ),
	},
	//
	// flags
	//
	isGoalDialogOpen: false,
	isInitialLoadComplete: false,
	isLoadingGoals: false,
	isResortingGoals: false,
	isSavingGoal: false,
	isSelectedGoalTeamChanging: false,
	//
	// state
	//
	currentLimit: 20,
	currentOffset: 0,
	/**
	 * Transient array used while editing a goal to keep track of key result ids that are
	 * being deleted. This is cleared when the dialog is closed or when a save completes.
	 * @type Array.<uiKey:String>
	 */
	deletingKeyResultsList: [],
	/**
	 * @type {Object.<goalId:String, inProgress:Boolean>}
	 */
	duplicateKeyResultErrors: {},
	/**
	 * The ID of the Goal being edited, if there is one.
	 */
	editingGoalId: "",
	/**
	 * Maps uiKeys to KeyResult objects for the GoalDialog component. The *order* of these
	 * objects is maintained in state.editingKeyResultsOrder.
	 * @type Object.<uiKey:String, KeyResult>
	 */
	editingKeyResultsMap: {},
	/**
	 * Maintains the *order* of key results in the Goal dialog, using a surrogate
	 * "uiKey" that is associated to key results in the state.editingKeyResultsMap.
	 * @type Array.<uiKey:String>
	 */
	editingKeyResultsOrder: [],
	// used to capture when a goal is clicked on during a meeting
	// with the intent to load the goal list to display for further analysis
	examinedGoal: null,
	/**
	 * Tracks which goal types have already been fetched so we don't fetch
	 * again unnecessarily.
	 */
	fetchedGoalTypes: [],
	initialMetadata: createEmptyMetadata(),
	/**
	 * Maps goalIds to arrays of KeyResults for that goalId.
	 * @type Object<goalId:String, Array.<KeyResult>>
	 */
	keyResultsMap: {},
	keyResultFetchErrors: {},
	keyResultFetchStatusMap: {},
	/**
	 * Tracks which key results are visible in the goal list; useful when
	 * switching between tabs, but expecting to see KRs expanded that a
	 * user had previously expanded.
	 * @type Object.<goalId:String, Boolean>
	 */
	keyResultVisibilityMap: {},
	/**
	* All of the current user's goals.
	*/
	myGoals: [],
	noMoreGoalsForType: false,
	/**
	 * The team selected for the goal being edited in the GoalDialog component.
	 */
	selectedGoalTeam: {},
	/**
	 * Determines which goals should be loaded when the user clicks a navigation
	 * tab at the top of the goals list page (i.e., active, draft, archived).
	 */
	selectedGoalType: GoalType.ACTIVE,
	selectedMeetingId: null,
	selectedTeamId: null,
	sortDirection: "asc",
};



// WEBPACK FOOTER //
// ./src/store/modules/goals/state.js